import { c as _c } from "react/compiler-runtime";
import { Copy } from "lucide-react";
import { useTranslations } from "next-intl";
type AgencyContactRowsProps = {
  phones?: string[];
  email?: string;
  website?: string | null;
  /** Listing reference shown (linking to its source) instead of the website. */
  reference?: string | null;
  /** Where the reference links to (the listing's source URL). */
  referenceUrl?: string | null;
  /** Used to seed the WhatsApp message. */
  title: string;
  url?: string;
  handleCopy: (value: string) => void;
};

/** The phone / email / website contact rows shared by the room-rent and
 *  sale agency cards: each row links out, phones starting with 9 expose a
 *  WhatsApp shortcut, and every row has a copy button. */
export function AgencyContactRows(t0) {
  const $ = _c(20);
  const {
    phones,
    email,
    website,
    reference,
    referenceUrl,
    title,
    url,
    handleCopy
  } = t0;
  const t = useTranslations("modules.realEstateDetail");
  let t1;
  if ($[0] !== handleCopy) {
    t1 = value => <div className="bg-muted hover:bg-muted-strong rounded-full w-6 h-6 flex items-center justify-center cursor-pointer shrink-0" onClick={() => handleCopy(value)} data-sentry-component="copyButton" data-sentry-source-file="AgencyContactRows.tsx"><Copy className="h-3 w-3 text-foreground-secondary" data-sentry-element="Copy" data-sentry-source-file="AgencyContactRows.tsx" /></div>;
    $[0] = handleCopy;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copyButton = t1;
  let t2;
  if ($[2] !== copyButton || $[3] !== phones || $[4] !== title || $[5] !== url) {
    t2 = phones?.map((phone, index) => {
      const hasWhatsapp = phone[0] === "9";
      return <div className="flex justify-between items-center gap-2 min-w-0" key={index}><a href={`tel:${phone}`} className="text-sm text-muted-foreground hover:underline truncate">{phone}</a><div className="flex items-center gap-1.5 shrink-0">{hasWhatsapp && <a href={`https://wa.me/351${phone}?text=${encodeURIComponent(`${title} - ${url}`)}`} target="_blank" rel="noopener noreferrer"><div className="bg-[#25D366] rounded-full w-6 h-6 flex items-center justify-center"><svg width="14" height="14" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.88 11.94L4 20l4.2-1.1a7.93 7.93 0 0 0 3.8.96h.01a7.95 7.95 0 0 0 7.94-7.93 7.88 7.88 0 0 0-2.35-5.61zm-5.55 12.2h-.01a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.5.65.67-2.43-.16-.25a6.6 6.6 0 0 1-1.01-3.49 6.59 6.59 0 0 1 6.6-6.59c1.76 0 3.42.69 4.66 1.93a6.57 6.57 0 0 1 1.94 4.66 6.59 6.59 0 0 1-6.59 6.58zm3.61-4.94c-.2-.1-1.17-.58-1.35-.64-.18-.07-.32-.1-.45.1-.13.2-.5.64-.62.77-.11.13-.23.15-.43.05a5.44 5.44 0 0 1-2.7-2.35c-.2-.35.2-.33.58-1.1.07-.13.03-.24-.02-.34-.05-.1-.45-1.08-.62-1.47-.16-.39-.32-.33-.45-.34h-.38c-.13 0-.35.05-.53.25-.18.2-.7.68-.7 1.66s.72 1.93.82 2.06c.1.13 1.4 2.14 3.4 3 .47.2.84.33 1.13.42.48.15.91.13 1.25.08.38-.06 1.17-.48 1.33-.94.17-.46.17-.86.12-.94-.05-.08-.18-.13-.38-.23z" /></svg></div></a>}{copyButton(phone)}</div></div>;
    });
    $[2] = copyButton;
    $[3] = phones;
    $[4] = title;
    $[5] = url;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== copyButton || $[8] !== email) {
    t3 = email && <div className="flex justify-between items-center gap-2 min-w-0"><a href={`mailto:${email}`} className="text-sm text-muted-foreground hover:underline truncate min-w-0">{email}</a>{copyButton(email)}</div>;
    $[7] = copyButton;
    $[8] = email;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== copyButton || $[11] !== reference || $[12] !== referenceUrl || $[13] !== t || $[14] !== website) {
    t4 = reference ? <a href={referenceUrl || website || undefined} target="_blank" rel="noopener noreferrer" className="self-end text-sm text-muted-foreground hover:text-foreground-strong transition-colors truncate max-w-full">{t("listingReference", {
        reference
      })}</a> : website && <div className="flex justify-between items-center gap-2 min-w-0"><a href={website} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:underline truncate min-w-0">{website.replace(/^https?:\/\//, "")}</a>{copyButton(website)}</div>;
    $[10] = copyButton;
    $[11] = reference;
    $[12] = referenceUrl;
    $[13] = t;
    $[14] = website;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t5 = <div className="flex flex-col gap-1.5" data-sentry-component="AgencyContactRows" data-sentry-source-file="AgencyContactRows.tsx">{t2}{t3}{t4}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}